/**
 * hovercraft_visuals.ts — CLIENT-ONLY per-model look of a hovercraft's
 * engines + dust (the catalog mirror stays byte-identical with the server, so
 * anything purely visual lives here, keyed by the same model keys).
 *
 * Every model gets a distinct engine signature — nozzle count, plume colour,
 * afterburner colour and dust throw — fitted to the actual hull by
 * `VehicleFx` from the model's bounding box (nozzles sit on the tail line,
 * spread across the hull's width; dust throws from the skirt's rear corners).
 */

export interface HovercraftVisual {
  /** Engine nozzles across the tail (1 = centre; n ≥ 2 spread ±spreadFrac × half-width). */
  nozzles: number;
  /** Fraction of the half-width the outermost nozzles sit at. */
  spreadFrac: number;
  /** Plume / core colour while cruising (hex). */
  color: number;
  /** Afterburner colour under SHIFT boost / a drift mini-turbo (hex). */
  boostColor: number;
  /** Plume length at cruise (m) — the jet's visible exhaust cone. */
  plumeLen: number;
  /** Nozzle mouth radius (m). */
  nozzleR: number;
  /** Dust throw multiplier (heavier skirts kick more). */
  dust: number;
}

export const HOVERCRAFT_VISUALS: Record<string, HovercraftVisual> = {
  hover_skiff:   { nozzles: 1, spreadFrac: 0,    color: 0xff8a3c, boostColor: 0xffe27a, plumeLen: 2.2, nozzleR: 0.34, dust: 1.15 },
  hover_dune:    { nozzles: 2, spreadFrac: 0.42, color: 0xff9d4a, boostColor: 0xfff0a8, plumeLen: 2.6, nozzleR: 0.30, dust: 1.3 },
  hover_falcon:  { nozzles: 2, spreadFrac: 0.5,  color: 0x4fc8ff, boostColor: 0xd8f6ff, plumeLen: 3.2, nozzleR: 0.28, dust: 0.9 },
  hover_raptor:  { nozzles: 3, spreadFrac: 0.55, color: 0x8a6cff, boostColor: 0xe6dcff, plumeLen: 3.6, nozzleR: 0.26, dust: 1.0 },
  hover_tempest: { nozzles: 4, spreadFrac: 0.6,  color: 0x9fe9ff, boostColor: 0xffffff, plumeLen: 4.2, nozzleR: 0.24, dust: 1.1 },
};

/** A visual for any key (unknown models get the skiff signature). */
export function hovercraftVisual(key: string): HovercraftVisual {
  return HOVERCRAFT_VISUALS[key] ?? HOVERCRAFT_VISUALS.hover_skiff;
}
